import { SelectionItem } from '../../../core/component/select/select.component';
import { DimensionLabels } from '../dimension-labels';

export enum AccountFunction {
  Other = 0,
  CustomerClaim = 1,
  OutgoingVat = 2,
  IngoingVat = 3,
  AdvancePayment = 4,
  OutboundVatPeriodization = 5,
}

export enum AccountType {
  Asset = 1,
  OwnCapitalAndDebt = 2,
  Income = 3,
  Expense = 4,
  OterExternalExpense = 5,
  OterExternalExpense2 = 6,
  ExpensePersonalAndDepreciation = 7,
  FinancialIncomeAndCost = 8,
}

export enum DimensionType {
  Undefined = 0,
  CostPlace = 1,
  Project = 2,
  CostBearer = 3,
  CounterPart = 4,
  Function = 5,
  FixedValue = 99,
}

export function createDimensionSelection(labels?: Partial<DimensionLabels>, includeFixedValue: boolean = false): SelectionItem[] {
  const result = [
    new SelectionItem(DimensionTypeToString(DimensionType.Undefined, labels), DimensionType.Undefined),
    new SelectionItem(DimensionTypeToString(DimensionType.CostPlace, labels), DimensionType.CostPlace),
    new SelectionItem(DimensionTypeToString(DimensionType.CostBearer, labels), DimensionType.CostBearer),
    new SelectionItem(DimensionTypeToString(DimensionType.Project, labels), DimensionType.Project),
    new SelectionItem(DimensionTypeToString(DimensionType.CounterPart, labels), DimensionType.CounterPart),
    new SelectionItem(DimensionTypeToString(DimensionType.Function, labels), DimensionType.Function),
  ];

  if (includeFixedValue) {
    result.push(new SelectionItem(DimensionTypeToString(DimensionType.FixedValue, labels), DimensionType.FixedValue));
  }

  return result;
}

export const dimensionSelection: SelectionItem[] = createDimensionSelection();

export function DimensionTypeToString(dimension: DimensionType, labels?: Partial<DimensionLabels>): string {
  switch (dimension) {
    case DimensionType.Undefined:
      return 'CONST.NONE';
    case DimensionType.CostPlace:
      return labels?.costPlaceLabel || 'ACCOUNT_TRANSACTION.COST_PLACE';
    case DimensionType.CostBearer:
      return labels?.costBearerLabel || 'ACCOUNT_TRANSACTION.COST_BEARER';
    case DimensionType.CounterPart:
      return labels?.counterpartLabel || 'ACCOUNT_TRANSACTION.COUNTERPART';
    case DimensionType.Function:
      return labels?.functionLabel || 'ACCOUNT_TRANSACTION.FUNCTION';
    case DimensionType.Project:
      return labels?.projectLabel || 'ACCOUNT_TRANSACTION.PROJECT';
    case DimensionType.FixedValue:
      return 'ACCOUNT_TRANSACTION.FIXED_VALUE';

    default:
      throw new Error(`Can't cast ${dimension} to string.`);
  }
}

export function AccountFunctionToString(func: AccountFunction): string {
  switch (func) {
    case AccountFunction.CustomerClaim:
      return 'CONST.ACCOUNT_FUNCTION_CUSTOMER_CLAIM';

    case AccountFunction.AdvancePayment:
      return 'CONST.ACCOUNT_FUNCTION_ADVANCE_PAYMENT';

    case AccountFunction.IngoingVat:
      return 'CONST.ACCOUNT_FUNCTION_INGOING_VAT';

    case AccountFunction.Other:
      return 'CONST.ACCOUNT_FUNCTION_OTHER';

    case AccountFunction.OutboundVatPeriodization:
      return 'CONST.ACCOUNT_FUNCTION_OUTBOUND_VAT_PERIODIZATION';

    case AccountFunction.OutgoingVat:
      return 'CONST.ACCOUNT_FUNCTION_OUTGOING_VAT';

    default:
      throw new Error(`Can't cast ${func} to string.`);
  }
}

export function AccountTypeToString(type: AccountType): string {
  switch (type) {
    case AccountType.Asset:
      return 'CONST.ACCOUNT_TYPE_ASSET';

    case AccountType.Expense:
      return 'CONST.ACCOUNT_TYPE_EXPENSE';

    case AccountType.ExpensePersonalAndDepreciation:
      return 'CONST.ACCOUNT_TYPE_EXPENSE_PERSONAL_AND_DEPRECIATION';

    case AccountType.FinancialIncomeAndCost:
      return 'CONST.ACCOUNT_TYPE_FINANCIAL_INCOME_AND_COST';

    case AccountType.Income:
      return 'CONST.ACCOUNT_TYPE_INCOME';

    case AccountType.OterExternalExpense:
      return 'CONST.ACCOUNT_TYPE_OTHER_EXTERNAL_EXPENSE';

    case AccountType.OterExternalExpense2:
      return 'CONST.ACCOUNT_TYPE_OTHER_EXTERNAL_EXPENSE2';

    case AccountType.OwnCapitalAndDebt:
      return 'CONST.ACCOUNT_TYPE_OWN_CAPITAL_AND_DEBT';

    default:
      throw new Error(`Can't cast ${type} to string.`);
  }
}
